// ============================================================
// DeathWatchSimulator - the composition root.
//
// Builds the SHELL SCOPE - one record holding every name the shell
// declares - by running the domain hooks in order: state, attacker,
// chrome, sync, map, table, attack, people, rail, controls
// (hooks/useShellDomain*). Each reads what it needs from the scope and
// returns what it declares, which is added to the scope before the next
// one runs; their hook calls are the shell's original sequence,
// unchanged, so every effect runs in the order it always did. Then the
// five context values (shell/useShellContexts) and the markup
// (shell/ShellProviders, shell/ShellMain). It carries no logic of its
// own - see dev-docs/architecture.md, "The shell's hooks and components".
// ============================================================

import { getDemoScenario } from '../engine/demoScenarios';
import useShellDomainState from '../hooks/useShellDomainState';
import useShellDomainAttacker from '../hooks/useShellDomainAttacker';
import useShellDomainChrome from '../hooks/useShellDomainChrome';
import useShellDomainSync from '../hooks/useShellDomainSync';
import useShellDomainAssets from '../hooks/useShellDomainAssets';
import useShellDomainMap from '../hooks/useShellDomainMap';
import useShellDomainTable from '../hooks/useShellDomainTable';
import useShellDomainAttack from '../hooks/useShellDomainAttack';
import useShellDomainPeople from '../hooks/useShellDomainPeople';
import useShellDomainRail from '../hooks/useShellDomainRail';
import useShellDomainControls from '../hooks/useShellDomainControls';
import useShellContexts from './shell/useShellContexts';
import useFuzzTap from '../hooks/useFuzzTap';
import ShellProviders from './shell/ShellProviders';
import ShellMain from './shell/ShellMain';
import { initialSessionOf, seatOf } from './shell/shellSession';
import { useStableHandlersOf } from '../hooks/useStableCallback';
import { isShellHandler } from './shell/shellHandlers';

// Dev/emulator-only demo scenario loader (?demo=full-combat|horde|crits).
// Resolved once at module load; null in production builds.
const demoScenario = getDemoScenario(
  typeof window !== 'undefined' ? window.location.search : '',
  import.meta.env.DEV || import.meta.env.VITE_USE_EMULATOR === 'true',
);

export default function DeathWatchSimulator({ user, session, setSession, onLeave }) {
  // One scope object per render, filled in domain by domain. It is the
  // SAME object throughout, so a callback may read a name a later domain
  // declares at call time - see useShellDomainSync's terrain-lift log.
  const shell = {
    user,
    session,
    setSession,
    onLeave,
    demoScenario,
    initialSession: initialSessionOf(demoScenario),
    ...seatOf(session, user),
  };
  Object.assign(shell, useShellDomainState(shell));
  Object.assign(shell, useShellDomainAttacker(shell));
  Object.assign(shell, useShellDomainChrome(shell));
  Object.assign(shell, useShellDomainSync(shell));
  // The GM's asset library at the table: after the sync, whose map library, loader and toasts it uses.
  Object.assign(shell, useShellDomainAssets(shell));
  Object.assign(shell, useShellDomainMap(shell));
  Object.assign(shell, useShellDomainTable(shell));
  Object.assign(shell, useShellDomainAttack(shell));
  Object.assign(shell, useShellDomainPeople(shell));
  Object.assign(shell, useShellDomainRail(shell));
  Object.assign(shell, useShellDomainControls(shell));
  // Its handlers made stable before anything is built from them: closures fresh each render gave every context a
  // new value, and every memoised panel reading one rendered with the whole table (2026-10-30).
  Object.assign(shell, useStableHandlersOf(shell, isShellHandler));
  const contexts = useShellContexts(shell);
  // Last, so every hook above keeps its place: the sandbox fuzzer's view of this table.
  useFuzzTap(shell);

  return (
    <ShellProviders contexts={contexts} shell={shell}>
      <ShellMain shell={shell} />
    </ShellProviders>
  );
}
